import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Layers,
  Maximize2,
  RotateCcw,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";

import Reveal from "@/components/public/Reveal";
import { publicApi } from "@/lib/api";

export default function BimPage() {
  const [page, setPage] = useState(null);
  const [services, setServices] = useState([]);
  const [activeTechnicalSection, setActiveTechnicalSection] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const imageContainerRef = useRef(null);

  useEffect(() => {
    publicApi.track({ event_type: "page_view", page: "/bim-production" }).catch(() => {});
    publicApi.getPage("bim").then(setPage).catch(() => {});
    publicApi.getServices("bim").then(setServices).catch(() => setServices([]));
  }, []);

  const hero = page?.content?.hero || {
    headline: "Let’s work together",
    subheadline: "Quality Revit© modeling and project management services for your office.",
    background: "/assets/minga/bim/point_cloud_gif1/02-2-as-built-point-cloud-with-3d-model.jpg",
    cta: { label: "Request More Info", link: "/contact" },
  };
  const intro = page?.content?.intro;
  const pm = page?.content?.project_management || [];
  const sequences = page?.content?.bim_sequences?.length
    ? page.content.bim_sequences
    : page?.content?.point_cloud
      ? [page.content.point_cloud]
      : [];
  const technicalSections = page?.content?.technical_sections?.length
    ? page.content.technical_sections
    : page?.content?.technical_gallery?.items?.length
      ? [page.content.technical_gallery]
      : [];
  const firstSequence = sequences[0];
  const sequencePreview = firstSequence?.steps?.find((step) => step.image);
  const safeTechnicalIndex = technicalSections.length
    ? Math.min(activeTechnicalSection, technicalSections.length - 1)
    : 0;
  const activeTechnical = technicalSections[safeTechnicalIndex];
  const activeTechnicalItems = activeTechnical?.items || [];
  const lightboxItems = lightbox ? technicalSections[lightbox.sectionIndex]?.items || [] : [];
  const lightboxItem = lightbox ? lightboxItems[lightbox.itemIndex] : null;

  // Reset zoom + pan whenever a different lightbox image is opened.
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [lightbox?.sectionIndex, lightbox?.itemIndex]);

  useEffect(() => {
    if (!lightbox) return undefined;

    const previousOverflow = document.body.style.overflow;
    const onKey = (event) => {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowLeft") navigateLightbox(-1);
      if (event.key === "ArrowRight") navigateLightbox(1);
      if (event.key === "+" || event.key === "=") adjustZoom(0.25);
      if (event.key === "-" || event.key === "_") adjustZoom(-0.25);
      if (event.key === "0") resetZoom();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox]);

  const openLightbox = (itemIndex) => {
    setLightbox({ sectionIndex: safeTechnicalIndex, itemIndex });
  };

  function navigateLightbox(direction) {
    setLightbox((current) => {
      if (!current) return current;
      const items = technicalSections[current.sectionIndex]?.items || [];
      if (!items.length) return current;
      return {
        ...current,
        itemIndex: (current.itemIndex + direction + items.length) % items.length,
      };
    });
  }

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;

  const clampPan = useCallback((next, currentZoom) => {
    const container = imageContainerRef.current;
    if (!container) return next;
    const rect = container.getBoundingClientRect();
    // Overflow beyond visible area when scaled by currentZoom.
    const maxX = (rect.width * (currentZoom - 1)) / 2;
    const maxY = (rect.height * (currentZoom - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  }, []);

  const adjustZoom = useCallback((delta) => {
    setZoom((current) => {
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number((current + delta).toFixed(2))));
      if (next <= MIN_ZOOM) {
        setPan({ x: 0, y: 0 });
      } else {
        setPan((p) => clampPan(p, next));
      }
      return next;
    });
  }, [clampPan]);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  // Attach non-passive wheel listener so we can preventDefault and stop page scroll while zooming.
  useEffect(() => {
    const node = imageContainerRef.current;
    if (!node || !lightbox) return undefined;
    const handler = (e) => {
      e.preventDefault();
      const delta = e.deltaY < 0 ? 0.2 : -0.2;
      adjustZoom(delta);
    };
    node.addEventListener("wheel", handler, { passive: false });
    return () => node.removeEventListener("wheel", handler);
  }, [lightbox, adjustZoom]);

  const onImageMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    isPanningRef.current = true;
    setIsPanning(true);
    panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!isPanningRef.current) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      setPan(() =>
        clampPan(
          { x: panStartRef.current.panX + dx, y: panStartRef.current.panY + dy },
          zoom
        )
      );
    };
    const onUp = () => {
      if (!isPanningRef.current) return;
      isPanningRef.current = false;
      setIsPanning(false);
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [zoom, clampPan]);

  // Touch pan + pinch inside lightbox.
  useEffect(() => {
    const node = imageContainerRef.current;
    if (!node || !lightbox) return undefined;

    let pinchStartDist = 0;
    let pinchStartZoom = 1;
    let touchStart = null;
    let panStart = { x: 0, y: 0 };

    const distance = (t0, t1) => {
      const dx = t0.clientX - t1.clientX;
      const dy = t0.clientY - t1.clientY;
      return Math.hypot(dx, dy);
    };

    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        pinchStartDist = distance(e.touches[0], e.touches[1]);
        pinchStartZoom = zoom;
      } else if (e.touches.length === 1 && zoom > 1) {
        touchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        panStart = { ...pan };
      }
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 2 && pinchStartDist > 0) {
        if (e.cancelable) e.preventDefault();
        const d = distance(e.touches[0], e.touches[1]);
        const scale = d / pinchStartDist;
        const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Number((pinchStartZoom * scale).toFixed(2))));
        setZoom(nextZoom);
        if (nextZoom <= MIN_ZOOM) setPan({ x: 0, y: 0 });
        else setPan((p) => clampPan(p, nextZoom));
      } else if (e.touches.length === 1 && touchStart && zoom > 1) {
        if (e.cancelable) e.preventDefault();
        const dx = e.touches[0].clientX - touchStart.x;
        const dy = e.touches[0].clientY - touchStart.y;
        setPan(clampPan({ x: panStart.x + dx, y: panStart.y + dy }, zoom));
      }
    };
    const onTouchEnd = () => {
      pinchStartDist = 0;
      touchStart = null;
    };

    node.addEventListener("touchstart", onTouchStart, { passive: true });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("touchend", onTouchEnd, { passive: true });
    node.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [lightbox, zoom, pan, clampPan]);

  return (
    <div>
      <section className="relative h-[85svh] min-h-[560px] w-full overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${hero.background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink-black/75 via-ink-black/45 to-ink-black" />
        <div className="absolute inset-0 blueprint opacity-30" />
        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
          <p className="eyebrow">{hero.eyebrow || "[ BIM Production ]"}</p>
          <h1 className="hero-headline mt-6 max-w-4xl">{hero.headline}</h1>
          <p className="body-text mt-6 max-w-2xl md:text-lg text-brand-white/85">
            {hero.subheadline}
          </p>
          <div className="mt-8 inline-flex">
            <Link to={hero.cta?.link || "/contact"} className="btn-primary" data-testid="bim-hero-cta">
              {hero.cta?.label || "Request More Info"} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {intro && (
        <section className="section-pad-sm max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <p className="body-text text-lg max-w-3xl">{intro}</p>
          </Reveal>
        </section>
      )}

      {(firstSequence || services.length) && (
        <section className="section-pad-sm max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
            {firstSequence ? (
              <Reveal className="surface surface-hover p-6 md:p-8 lg:p-10 h-full min-h-[620px] flex flex-col">
                <p className="eyebrow">[3D Scan to 3D Model]</p>
                <h2 className="section-headline mt-4 max-w-2xl">
                  {firstSequence.title || "Point Cloud to Model"}
                </h2>
                {firstSequence.description ? (
                  <p className="body-text mt-5 max-w-2xl">{firstSequence.description}</p>
                ) : null}

                {sequencePreview?.image ? (
                  <div className="relative mt-8 flex-1 min-h-[320px] overflow-hidden bg-ink-graphite image-hover-zoom">
                    <img
                      src={sequencePreview.image}
                      alt={sequencePreview.label || firstSequence.title || "Sequence 01 preview"}
                      loading="lazy"
                      className="h-full w-full object-cover opacity-95"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-black/70 via-transparent to-transparent" />
                    <div className="absolute top-5 left-5 eyebrow bg-ink-black/70 px-3 py-1.5">
                      {sequencePreview.label || "Preview"}
                    </div>
                  </div>
                ) : null}
              </Reveal>
            ) : null}

            <Reveal delay={100} className="surface surface-hover p-6 md:p-8 lg:p-10 h-full min-h-[620px]">
              <p className="eyebrow">[ BIM Services ]</p>
              <h2 className="section-headline mt-4">What we deliver.</h2>
              <div className="grid sm:grid-cols-2 gap-4 mt-10">
                {services.map((s, i) => (
                  <Reveal
                    key={s.id || s.title}
                    delay={i * 70}
                    className="relative surface p-5 md:p-6 min-h-[190px] overflow-hidden border-white/10"
                  >
                    {s.image ? (
                      <div className="absolute inset-0 opacity-[0.13] grayscale">
                        <img src={s.image} alt="" className="h-full w-full object-cover" loading="lazy" />
                      </div>
                    ) : null}
                    <div className="relative z-10">
                      <Layers className="h-6 w-6 text-brand-yellow" />
                      <h3 className="headline text-lg mt-5">{s.title}</h3>
                      {s.description ? <p className="body-text text-sm mt-3">{s.description}</p> : null}
                    </div>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {technicalSections.length ? (
        <section className="section-pad">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10">
            <Reveal className="mb-8">
              <p className="eyebrow">[ Technical Gallery ]</p>
            </Reveal>

            <Reveal className="flex flex-wrap gap-2 md:gap-3" delay={90}>
              {technicalSections.map((section, index) => {
                const active = index === safeTechnicalIndex;

                return (
                  <button
                    key={section.title || index}
                    type="button"
                    onClick={() => setActiveTechnicalSection(index)}
                    className={`px-4 py-3 text-[11px] md:text-xs uppercase tracking-[0.18em] font-medium border transition-all duration-300 ${
                      active
                        ? "border-brand-yellow bg-brand-yellow text-ink-black"
                        : "border-white/10 text-brand-gray bg-white/[0.025] hover:border-brand-yellow/60 hover:text-brand-white"
                    }`}
                  >
                    {section.title || `Section ${index + 1}`}
                  </button>
                );
              })}
            </Reveal>

            {activeTechnical ? (
              <div className="mt-10 surface p-5 md:p-8 lg:p-10">
                <Reveal className="mb-10">
                  <p className="eyebrow">[ Technical Gallery ]</p>
                  <h2 className="section-headline mt-4 max-w-3xl">{activeTechnical.title}</h2>
                  {activeTechnical.description ? (
                    <p className="body-text mt-5 max-w-2xl">{activeTechnical.description}</p>
                  ) : null}
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {activeTechnicalItems.map((it, i) => (
                    <Reveal
                      key={`${activeTechnical.title}-${i}`}
                      delay={i * 70}
                      className="surface surface-hover overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => openLightbox(i)}
                        className="relative aspect-[4/3] overflow-hidden bg-ink-graphite image-hover-zoom cursor-pointer w-full text-left group"
                        aria-label={`Open ${it.title || "technical image"}`}
                      >
                        <img src={it.image} alt={it.title} loading="lazy" className="h-full w-full object-cover" />
                        <span className="absolute right-4 bottom-4 bg-ink-black/75 text-brand-white text-[10px] uppercase tracking-[0.18em] px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          View image
                        </span>
                      </button>
                      <div className="p-5">
                        <h3 className="headline text-lg">{it.title}</h3>
                        {it.description ? <p className="body-text text-sm mt-2">{it.description}</p> : null}
                        {it.disclaimer ? (
                          <p className="mt-3 text-[10px] uppercase tracking-[0.16em] text-brand-gray/70">
                            {it.disclaimer}
                          </p>
                        ) : null}
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {pm?.length ? (
        <section className="section-pad bg-brand-green text-ink-black border-y border-ink-black/10">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10">
            <Reveal className="mb-10">
              <p className="eyebrow !text-ink-black/70">[ Project Management ]</p>
              <h2 className="section-headline mt-4 max-w-3xl text-ink-black">Coordinated end to end.</h2>
            </Reveal>
            <div className="grid md:grid-cols-2 gap-4">
              {pm.map((label, i) => (
                <Reveal
                  key={label}
                  delay={i * 60}
                  className="p-5 md:p-6 flex items-start gap-4 border border-ink-black/15 bg-ink-black/5"
                >
                  <span className="shrink-0 mt-1 text-xs font-bold uppercase tracking-[0.24em] text-ink-black/65">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="body-text !text-ink-black/80">{label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="section-pad">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <h2 className="section-headline">Ready to scale your studio’s output?</h2>
            <p className="body-text mt-5 max-w-xl mx-auto">
              Request more info and let’s design a BIM production workflow that fits your firm.
            </p>
            <div className="mt-8 inline-flex">
              <Link to="/contact" className="btn-primary">
                Request More Info <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {lightboxItem ? (
        <div className="fixed inset-0 z-[80] bg-black/92 backdrop-blur-sm flex flex-col" role="dialog" aria-modal="true">
          <div className="flex items-center justify-between gap-3 p-4 md:p-5 border-b border-white/10">
            <div className="min-w-0">
              <p className="eyebrow-muted truncate">[ {technicalSections[lightbox.sectionIndex]?.title || "Technical Gallery"} ]</p>
              <h3 className="text-sm md:text-base font-medium truncate mt-1">{lightboxItem.title}</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="h-10 w-10 grid place-items-center border border-white/15 text-brand-white hover:border-brand-yellow hover:text-brand-yellow disabled:opacity-40"
                onClick={() => adjustZoom(-0.25)}
                aria-label="Zoom out"
                disabled={zoom <= MIN_ZOOM}
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="h-10 min-w-[64px] px-3 grid place-items-center border border-white/15 text-brand-white text-[11px] font-medium tracking-wider hover:border-brand-yellow hover:text-brand-yellow"
                onClick={resetZoom}
                aria-label="Reset zoom to fit"
                title="Reset (fit to viewport)"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                className="h-10 w-10 grid place-items-center border border-white/15 text-brand-white hover:border-brand-yellow hover:text-brand-yellow disabled:opacity-40"
                onClick={() => adjustZoom(0.25)}
                aria-label="Zoom in"
                disabled={zoom >= MAX_ZOOM}
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="h-10 w-10 grid place-items-center border border-white/15 text-brand-white hover:border-brand-yellow hover:text-brand-yellow"
                onClick={resetZoom}
                aria-label="Fit to screen"
                title="Fit to screen"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="h-10 w-10 grid place-items-center border border-white/15 text-brand-white hover:border-white hover:text-brand-red"
                onClick={() => setLightbox(null)}
                aria-label="Close gallery"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="relative flex-1 min-h-0 grid place-items-center p-4 md:p-6">
            {lightboxItems.length > 1 ? (
              <button
                type="button"
                className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 md:h-12 md:w-12 grid place-items-center border border-white/15 bg-black/50 text-brand-white hover:text-brand-yellow"
                onClick={() => navigateLightbox(-1)}
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            ) : null}

            <div
              ref={imageContainerRef}
              onMouseDown={onImageMouseDown}
              onDoubleClick={() => (zoom > 1 ? resetZoom() : adjustZoom(1))}
              className="relative flex items-center justify-center overflow-hidden select-none"
              style={{
                width: "92vw",
                height: "82vh",
                cursor:
                  zoom > 1
                    ? isPanning
                      ? "grabbing"
                      : "grab"
                    : "zoom-in",
                touchAction: zoom > 1 ? "none" : "pan-y",
              }}
            >
              <img
                src={lightboxItem.image}
                alt={lightboxItem.title}
                draggable={false}
                className="block object-contain transition-transform duration-150 ease-out will-change-transform pointer-events-none"
                style={{
                  maxWidth: "92vw",
                  maxHeight: "82vh",
                  width: "auto",
                  height: "auto",
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: "center center",
                }}
              />
            </div>

            {lightboxItems.length > 1 ? (
              <button
                type="button"
                className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 h-11 w-11 md:h-12 md:w-12 grid place-items-center border border-white/15 bg-black/50 text-brand-white hover:text-brand-yellow"
                onClick={() => navigateLightbox(1)}
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            ) : null}
          </div>

          <div className="px-4 md:px-5 py-3 border-t border-white/10 flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] text-brand-gray">
            <span>{lightbox.itemIndex + 1} / {lightboxItems.length}</span>
            {lightboxItem.description ? (
              <span className="truncate max-w-[55%] text-brand-white/85 normal-case tracking-normal">
                {lightboxItem.description}
              </span>
            ) : <span />}
            <span className="hidden md:inline text-brand-gray/80">
              Scroll · pinch to zoom · drag to pan · double-click to toggle
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
